import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import actions from "../../Redux Store/actions";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TranslateIcon from "@mui/icons-material/Translate";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { successTost, errorTost } from "../../Services/Toast";
import { supabase } from "../../supabaseClient";
import PersonaTeamList from "./PersonaTeamList";

/* ── Design tokens ──────────────────────────────────────────────────────── */
const C = {
  indigo: "#4338ca", indigo2: "#6366f1", indigoSoft: "#eef2ff", indigoBd: "#c7d2fe",
  green: "#16a34a", greenSoft: "#f0fdf4", greenBd: "#bbf7d0",
  red: "#dc2626", redSoft: "#fef2f2", redBd: "#fecaca",
  amber: "#d97706", amberSoft: "#fffbeb", amberBd: "#fde68a",
  sky: "#0284c7", skySoft: "#f0f9ff", skyBd: "#bae6fd",
  page: "#f8fafc", white: "#ffffff", border: "#e2e8f0", border2: "#f1f5f9",
  txt1: "#0f172a", txt2: "#334155", txt3: "#64748b", txt4: "#94a3b8",
  sans: "'Geist', system-ui, sans-serif", mono: "'Geist Mono', monospace",
};

const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800&family=Geist+Mono:wght@400;500&display=swap');
@keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
`;

const PAGE_BG = `
  radial-gradient(circle at 1px 1px, rgba(67,56,202,0.05) 1px, transparent 1px) 0 0 / 18px 18px,
  linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)
`;

/* ── Hierarchy helpers (mirrors CallLogsMain.jsx) ───────────────────────── */
function getAllDescendantIds(all, rootId) {
  const seen = new Set(), q = [rootId];
  while (q.length) {
    const id = q.shift();
    if (seen.has(id)) continue;
    seen.add(id);
    all.filter((u) => u.parentId === id).forEach((u) => q.push(u.userId));
  }
  return [...seen];
}

function getLeafIds(all, candidateIds) {
  const parentIdSet = new Set(
    all.filter((u) => u.parentId !== null).map((u) => u.parentId)
  );
  return candidateIds.filter((id) => !parentIdSet.has(id));
}

/* ── Styled atoms ────────────────────────────────────────────────────────── */
const PrimaryBtn = styled("button")(({ disabled }) => ({
  border: "none",
  background: disabled
    ? C.txt4
    : "linear-gradient(135deg, #4338ca 0%, #6366f1 100%)",
  color: C.white,
  padding: "11px 26px",
  borderRadius: 3,
  fontFamily: C.sans,
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: 0.2,
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "all 0.15s ease",
  boxShadow: disabled ? "none" : "0 4px 14px rgba(67,56,202,0.28)",
  whiteSpace: "nowrap",
  "&:hover": {
    background: disabled
      ? C.txt4
      : "linear-gradient(135deg, #3730a3 0%, #4f46e5 100%)",
  },
}));

const GhostBtn = styled("button")({
  border: `1.5px solid ${C.border}`,
  background: C.white,
  color: C.txt2,
  padding: "10px 20px",
  borderRadius: 8,
  fontFamily: C.sans,
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  transition: "all 0.15s ease",
  whiteSpace: "nowrap",
  "&:hover": { background: C.border2, borderColor: C.txt4 },
});

const BackBtn = styled("button")({
  border: `1px solid ${C.border}`,
  background: C.white,
  color: C.txt2,
  width: 36, height: 36,
  borderRadius: 8,
  fontFamily: C.sans,
  fontWeight: 600,
  fontSize: 18,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.15s ease",
  flexShrink: 0,
  "&:hover": { background: C.border2, borderColor: C.txt4 },
});

const AddBtn = styled("button")(({ disabled }) => ({
  border: `1.5px solid ${disabled ? C.border : C.indigo}`,
  background: disabled ? C.white : C.indigo,
  color: disabled ? C.txt4 : C.white,
  padding: "9px 18px",
  borderRadius: 8,
  fontFamily: C.sans,
  fontWeight: 600,
  fontSize: 13.5,
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "all 0.15s ease",
  whiteSpace: "nowrap",
  minHeight: 40,
  "&:hover": { background: disabled ? C.white : "#3730a3" },
}));

const TogglePill = styled("button", {
  shouldForwardProp: (p) => p !== "active",
})(({ active }) => ({
  border: `1.5px solid ${active ? C.indigo : C.border}`,
  background: active ? C.indigoSoft : C.white,
  color: active ? C.indigo : C.txt2,
  padding: "7px 14px",
  borderRadius: 6,
  fontFamily: C.sans,
  fontWeight: active ? 600 : 500,
  fontSize: 12.5,
  cursor: "pointer",
  transition: "all 0.15s ease",
  outline: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: C.indigo,
    background: active ? C.indigoSoft : "#f5f3ff",
  },
}));

/* ── Static option sets ──────────────────────────────────────────────────── */
const LANGUAGES = [
  "English", "Hindi", "Marathi", "Gujarati", "Punjabi",
  "Tamil", "Telugu", "Kannada", "Bengali", "Malayalam", "Urdu",
];

const PURPOSES = [
  { label: "Self Use", value: "self use" },
  { label: "Investor", value: "Investor" },
];
const PURPOSE_LABEL = Object.fromEntries(PURPOSES.map((p) => [p.value, p.label]));

const GENDER_PREFS = [
  { label: "Male",   value: "Male" },
  { label: "Female", value: "Female" },
  { label: "Any",    value: "Any" },
];

/* ── Shared MUI sx ───────────────────────────────────────────────────────── */
const acSx = {
  fontFamily: C.sans,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontFamily: C.sans,
    fontSize: 13.5,
    background: C.white,
    padding: "5px 10px !important",
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.indigoBd },
    "&.Mui-focused fieldset": { borderColor: C.indigo, borderWidth: 1.5 },
  },
  "& .MuiOutlinedInput-input": { padding: "6px 4px !important" },
  "& .MuiInputLabel-root": { fontFamily: C.sans, fontSize: 13.5, color: C.txt3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.indigo },
};

const chipSx = {
  background: C.indigoSoft,
  color: C.indigo,
  fontFamily: C.sans,
  fontWeight: 600,
  fontSize: 12.5,
  height: 26,
  borderRadius: "6px",
  border: `1px solid ${C.indigoBd}`,
  "& .MuiChip-deleteIcon": {
    color: C.indigo,
    fontSize: 16,
    "&:hover": { color: "#3730a3" },
  },
};

const projectChipSx = {
  background: C.white,
  color: C.txt2,
  fontFamily: C.sans,
  fontWeight: 500,
  fontSize: 12,
  height: 26,
  borderRadius: "6px",
  border: `1px solid ${C.skyBd}`,
};

/* ── Section card ────────────────────────────────────────────────────────── */
function Section({ index, question, hint, icon, children, compact }) {
  return (
    <Box
      sx={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: { xs: 8, md: 10 },
        p: compact ? { xs: 1.75, md: 2.25 } : { xs: 2, md: 2.75 },
        animation: "fadeUp 0.35s ease both",
        animationDelay: `${(index || 0) * 0.04}s`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: compact ? 1.25 : 1.5 }}>
        {index != null && (
          <Box
            sx={{
              width: compact ? 28 : 32, height: compact ? 28 : 32,
              borderRadius: 8,
              background: C.indigoSoft,
              color: C.indigo,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: C.sans, fontWeight: 700,
              fontSize: compact ? 13 : 14.5,
              flexShrink: 0,
            }}
          >
            {index}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: C.sans, fontWeight: 700,
              fontSize: compact ? 14 : { xs: 15, md: 16 },
              color: C.txt1, lineHeight: 1.3,
            }}
          >
            {question}
          </Typography>
          {hint && (
            <Typography
              sx={{
                fontFamily: C.sans,
                fontSize: compact ? 12 : 12.75,
                color: C.txt3, mt: 0.25, lineHeight: 1.4,
              }}
            >
              {hint}
            </Typography>
          )}
        </Box>
        {icon && (
          <Box
            sx={{
              width: 34, height: 34,
              borderRadius: 7,
              background: C.indigoSoft,
              color: C.indigo,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
      <Box>{children}</Box>
    </Box>
  );
}

/* ── Multi-select helper ─────────────────────────────────────────────────── */
function MultiSelect({ options, value, onChange, placeholder = "Search and add…", getOptionLabel, isOptionEqualToValue }) {
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      options={options}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderTags={(val, getTagProps) =>
        val.map((option, index) => {
          const key = typeof option === "object" ? option.value : option;
          const label = typeof option === "object" ? option.label : option;
          return (
            <Chip key={key} label={label} {...getTagProps({ index })} sx={chipSx} />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? placeholder : ""}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Box component="span" sx={{ color: C.txt4, fontSize: 16, pl: 0.5, pr: 0.5, display: "inline-flex", alignItems: "center" }}>⌕</Box>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      sx={acSx}
    />
  );
}

function ViewChips({ values, labelMap }) {
  if (!values || values.length === 0) {
    return (
      <Typography sx={{ fontFamily: C.sans, fontSize: 13, color: C.txt4, fontStyle: "italic" }}>
        Not configured
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {values.map((v) => (
        <Chip key={v} label={labelMap ? labelMap[v] || v : v} sx={chipSx} />
      ))}
    </Box>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
function SalesManagerPersona() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loggedInUser = useSelector((s) => s.Auth?.user);
  const myUserId = loggedInUser?.userId;

  useEffect(() => { dispatch(actions.activeMenu("salesmanagerpersona")); }, [dispatch]);

  // 'loading' | 'list' | 'view' | 'edit'
  const [mode, setMode] = useState("loading");

  // Hierarchy / team
  const [isLeafUser, setIsLeafUser] = useState(false);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]); // [{ userId, name, configured }]

  // Selected member (the user whose persona is being viewed/edited)
  const [selectedUser, setSelectedUser] = useState(null); // { userId, name }
  const [hasExisting, setHasExisting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Auto-fetched assigned projects (for selected user)
  const [projectIds, setProjectIds] = useState([]);
  const [projectNames, setProjectNames] = useState([]);

  // Persona selections (for selected user)
  const [languages, setLanguages]     = useState([]);
  const [locations, setLocations]     = useState([]);
  const [unitTypes, setUnitTypes]     = useState([]);
  const [budgetBands, setBudgetBands] = useState([]);
  const [purposes, setPurposes]       = useState([]);
  const [genderPrefs, setGenderPrefs] = useState([]);

  // Unit types from InventoryType table
  const [unitTypeOptions, setUnitTypeOptions]   = useState([]);
  const [unitTypesLoading, setUnitTypesLoading] = useState(false);

  // Budget options from Budget table (same source AddLead uses)
  const [budgetOptions, setBudgetOptions]   = useState([]); // [{ budgetId, budget }]
  const [budgetLoading, setBudgetLoading]   = useState(false);

  // Hierarchical location picker
  const [states, setStates]               = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [stateValue, setStateValue]       = useState(null);
  const [cityOptions, setCityOptions]     = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [cityValue, setCityValue]         = useState(null);
  const [pincodeOptions, setPincodeOptions]   = useState([]);
  const [pincodesLoading, setPincodesLoading] = useState(false);
  const [pincodeValue, setPincodeValue]       = useState(null);

  /* ── Step 1: Resolve hierarchy + build team list ──────────────────────── */
  const loadTeam = useCallback(async () => {
    if (!myUserId) return;
    setTeamLoading(true);
    setMode("loading");
    try {
      const { data: allUsers, error } = await supabase
        .from("Users")
        .select("userId, name, parentId, isActive")
        .eq("isActive", true);
      if (error) throw error;

      const me = allUsers.find((u) => u.userId === myUserId);
      const hasParent = me?.parentId !== null && me?.parentId !== undefined;
      const hasChildren = allUsers.some((u) => u.parentId === myUserId);

      let visibleIds = [];
      if (!hasParent) {
        // Root → sees all leaf users in the org
        visibleIds = getLeafIds(allUsers, allUsers.map((u) => u.userId));
      } else if (!hasChildren) {
        // Leaf → has nobody to configure
        setIsLeafUser(true);
        setTeamMembers([]);
        setTeamLoading(false);
        setMode("list");
        return;
      } else {
        // Mid-level manager → all leaf descendants
        const allDescendants = getAllDescendantIds(allUsers, myUserId);
        visibleIds = getLeafIds(allUsers, allDescendants).filter((id) => id !== myUserId);
      }

      setIsLeafUser(false);

      // Filter to active users only
      const visibleSet = new Set(visibleIds);
      const members = allUsers
        .filter((u) => visibleSet.has(u.userId))
        .map((u) => ({ userId: u.userId, name: u.name || "Unnamed", configured: false }))
        .sort((a, b) => a.name.localeCompare(b.name));

      // Find which members already have any persona row
      if (members.length > 0) {
        const ids = members.map((m) => m.userId);
        const tables = [
          "PreferenceLanguages", "PreferenceLocations", "PreferenceUnitTypes",
          "PreferenceBudgetBands", "PreferencePurposes", "PreferenceGenders",
        ];
        const results = await Promise.all(
          tables.map((t) => supabase.from(t).select(`"userId"`).in(`"userId"`, ids))
        );
        const configuredSet = new Set();
        results.forEach(({ data }) => (data || []).forEach((r) => configuredSet.add(r.userId)));
        members.forEach((m) => { m.configured = configuredSet.has(m.userId); });
      }

      setTeamMembers(members);
      setTeamLoading(false);
      setMode("list");
    } catch (e) {
      errorTost("Could not load your team");
      setTeamLoading(false);
      setTeamMembers([]);
      setMode("list");
    }
  }, [myUserId]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  /* ── Step 2: Load selected user's persona ────────────────────────────── */
  const loadPersona = useCallback(async (forUser) => {
    const target = forUser || selectedUser;
    if (!target?.userId) return;
    setMode("loading");
    try {
      const uid = target.userId;
      const [
        langRes, locRes, utRes, bbRes, purRes, genRes, assignedRes,
      ] = await Promise.all([
        supabase.from("PreferenceLanguages").select("language").eq("userId", uid).is("deletedAt", null),
        supabase.from("PreferenceLocations").select("location").eq("userId", uid).is("deletedAt", null),
        supabase.from("PreferenceUnitTypes").select(`"unitType"`).eq("userId", uid).is("deletedAt", null),
        supabase.from("PreferenceBudgetBands").select(`"budgetBand"`).eq("userId", uid).is("deletedAt", null),
        supabase.from("PreferencePurposes").select("purpose").eq("userId", uid).is("deletedAt", null),
        supabase.from("PreferenceGenders").select(`"genderPref"`).eq("userId", uid).is("deletedAt", null),
        supabase
          .from("SustenanceSalesRelations")
          .select(`projectId, Projects ( projectName )`)
          .eq("userId", uid),
      ]);

      const langs = (langRes?.data || []).map((r) => r.language).filter(Boolean);
      const locs  = (locRes?.data || []).map((r) => r.location).filter(Boolean);
      const uts   = (utRes?.data || []).map((r) => r.unitType).filter(Boolean);
      const bbs   = (bbRes?.data || []).map((r) => r.budgetBand).filter(Boolean);
      const purs  = (purRes?.data || []).map((r) => r.purpose).filter(Boolean);
      const gens  = (genRes?.data || []).map((r) => r.genderPref).filter(Boolean);

      const seenP = new Set();
      const pids = [];
      const pnames = [];
      (assignedRes?.data || []).forEach((row) => {
        if (row?.projectId && !seenP.has(row.projectId)) {
          seenP.add(row.projectId);
          pids.push(row.projectId);
          pnames.push(row?.Projects?.projectName || "Unnamed project");
        }
      });

      setLanguages(langs);
      setLocations(locs);
      setUnitTypes(uts);
      setBudgetBands(bbs);
      setPurposes(purs);
      setGenderPrefs(gens);
      setProjectIds(pids);
      setProjectNames(pnames);

      const existing = langs.length || locs.length || uts.length ||
        bbs.length || purs.length || gens.length;
      setHasExisting(Boolean(existing));
      setMode(existing ? "view" : "edit");
    } catch (err) {
      errorTost("Could not load saved persona");
      setMode("edit");
    }
  }, [selectedUser]);

  /* ── Load Budget options once (used in both view and edit modes) ──────── */
  useEffect(() => {
    let cancelled = false;
    setBudgetLoading(true);
    supabase
      .from("Budget")
      .select("budgetId, budget")
      .order("budget", { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          errorTost("Could not load budget options");
          setBudgetOptions([]);
        } else {
          setBudgetOptions(
            (data || []).filter((r) => r.budgetId && (r.budget || "").trim())
          );
        }
        setBudgetLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  /* ── Lazy-load unit types from InventoryType when entering edit mode ──── */
  useEffect(() => {
    if (mode !== "edit" || unitTypeOptions.length > 0) return;
    let cancelled = false;
    setUnitTypesLoading(true);
    supabase.from("InventoryType").select("type").order("type", { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          errorTost("Could not load unit types");
          setUnitTypeOptions([]);
        } else {
          const uniq = Array.from(
            new Set((data || []).map((r) => (r.type || "").trim()).filter(Boolean))
          ).sort((a, b) => a.localeCompare(b));
          setUnitTypeOptions(uniq);
        }
        setUnitTypesLoading(false);
      });
    return () => { cancelled = true; };
  }, [mode, unitTypeOptions.length]);

  /* ── Lazy-load states when entering edit mode ─────────────────────────── */
  useEffect(() => {
    if (mode !== "edit" || states.length > 0) return;
    let cancelled = false;
    setStatesLoading(true);
    supabase.from("indiaPincodes").select("state").order("state", { ascending: true }).range(0, 199999)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { errorTost("Could not load states list"); setStates([]); }
        else {
          const uniq = Array.from(
            new Set((data || []).map((r) => (r.state || "").trim()).filter(Boolean))
          ).sort((a, b) => a.localeCompare(b));
          setStates(uniq);
        }
        setStatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [mode, states.length]);

  useEffect(() => {
    setCityValue(null); setCityOptions([]);
    setPincodeValue(null); setPincodeOptions([]);
    if (!stateValue || mode !== "edit") return;
    let cancelled = false;
    setCitiesLoading(true);
    supabase.from("indiaPincodes").select("district").eq("state", stateValue).order("district", { ascending: true }).range(0, 49999)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { errorTost("Could not load cities"); setCityOptions([]); }
        else {
          const uniq = Array.from(
            new Set((data || []).map((r) => (r.district || "").trim()).filter(Boolean))
          ).sort((a, b) => a.localeCompare(b));
          setCityOptions(uniq);
        }
        setCitiesLoading(false);
      });
    return () => { cancelled = true; };
  }, [stateValue, mode]);

  useEffect(() => {
    setPincodeValue(null); setPincodeOptions([]);
    if (!stateValue || !cityValue || mode !== "edit") return;
    let cancelled = false;
    setPincodesLoading(true);
    supabase.from("indiaPincodes").select("pincode").eq("state", stateValue).eq("district", cityValue).order("pincode", { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) setPincodeOptions([]);
        else {
          const uniq = Array.from(
            new Set((data || []).map((r) => String(r.pincode).trim()).filter(Boolean))
          );
          setPincodeOptions(uniq);
        }
        setPincodesLoading(false);
      });
    return () => { cancelled = true; };
  }, [stateValue, cityValue, mode]);

  const addLocation = () => {
    if (!stateValue || !cityValue) {
      errorTost("Please pick a state and city first"); return;
    }
    const valueToSave = pincodeValue || cityValue;
    setLocations((prev) => (prev.includes(valueToSave) ? prev : [...prev, valueToSave]));
    setPincodeValue(null);
  };

  const totalSelected = useMemo(
    () =>
      languages.length + locations.length + unitTypes.length +
      budgetBands.length + purposes.length + genderPrefs.length,
    [languages, locations, unitTypes, budgetBands, purposes, genderPrefs],
  );

  const handleSave = async () => {
    if (!selectedUser?.userId) { errorTost("No user selected"); return; }
    if (totalSelected === 0) { errorTost("Please select at least one preference before saving"); return; }
    setSaving(true);
    try {
      const baseUrl = process.env.REACT_APP_PAYMENT_LINK_URL;
      const personaUrl = `${baseUrl}matchmaking/persona/${selectedUser.userId}`;
      await axios.patch(personaUrl, {
        languages, projectIds, locations, unitTypes,
        budgetBands, purposes, genderPrefs,
      });
      successTost(`${selectedUser.name}'s persona saved successfully!`);
      // T2 trigger: rescore this user against every unclaimed lead.
      let rescoreMsg = "";
      try {
        const rescoreUrl = `${baseUrl}matchmaking/triggers/user/${selectedUser.userId}/rescore`;
        const { data: rescoreRes } = await axios.post(rescoreUrl);
        if (rescoreRes?.written != null) {
          rescoreMsg = ` · ${rescoreRes.written} lead${rescoreRes.written === 1 ? "" : "s"} rescored`;
        }
      } catch (rescoreErr) {
        // Non-fatal — persona was saved; the backend's debounced rescore will catch up.
        console.warn("Rescore trigger failed:", rescoreErr);
      }

      
      // Mark as configured locally and reload the persona
      setTeamMembers((prev) =>
        prev.map((m) =>
          m.userId === selectedUser.userId ? { ...m, configured: true } : m
        )
      );
      await loadPersona(selectedUser);
    } catch (err) {
      errorTost(err?.response?.data?.error || "Failed to save persona");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasExisting) loadPersona(selectedUser);
    else goBackToList();
  };

  const goBackToList = () => {
    setSelectedUser(null);
    setLanguages([]); setLocations([]); setUnitTypes([]);
    setBudgetBands([]); setPurposes([]); setGenderPrefs([]);
    setProjectIds([]); setProjectNames([]);
    setStateValue(null); setCityValue(null); setPincodeValue(null);
    setHasExisting(false);
    setMode("list");
  };

  const openMember = (member) => {
    setSelectedUser({ userId: member.userId, name: member.name });
    loadPersona({ userId: member.userId, name: member.name });
  };

  /* ── Header ──────────────────────────────────────────────────────────── */
  const isPersonaScreen = mode === "view" || mode === "edit";
  const Header = (
    <Box
      sx={{
        mb: { xs: 2, md: 2.5 },
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 1.25, md: 2 },
      }}
    >
      <BackBtn
        type="button"
        onClick={() => (isPersonaScreen ? goBackToList() : navigate(-1))}
        title="Back"
      >
        ←
      </BackBtn>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: C.sans, fontWeight: 700,
            fontSize: { xs: 19, md: 24 }, color: C.txt1, lineHeight: 1.2,
          }}
        >
          {isPersonaScreen
            ? `${selectedUser?.name || "Member"}'s Persona`
            : "Team Personas"}
        </Typography>
        <Typography
          sx={{
            fontFamily: C.sans,
            fontSize: { xs: 12.5, md: 14 }, color: C.txt3, mt: 0.5,
          }}
        >
          {isPersonaScreen
            ? (mode === "view"
              ? `Configured persona for ${selectedUser?.name}. Click Edit to refine matching.`
              : `You're configuring ${selectedUser?.name}'s persona on their behalf.`)
            : "Pick a sales manager from your team to configure their lead-matching persona."}
        </Typography>
      </Box>
    </Box>
  );

  /* ── Loading state ───────────────────────────────────────────────────── */
  if (mode === "loading") {
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        <Box
          sx={{
            height: "100%",
            background: PAGE_BG,
            fontFamily: C.sans,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <CircularProgress size={18} sx={{ color: C.indigo }} />
          <Typography sx={{ fontFamily: C.sans, fontSize: 14, color: C.txt3 }}>
            {teamLoading ? "Loading your team…" : "Loading persona…"}
          </Typography>
        </Box>
      </>
    );
  }

  /* ── LIST MODE ───────────────────────────────────────────────────────── */
  if (mode === "list") {
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        <Box
          sx={{
            height: "100%",
            overflowY: "auto",
            background: PAGE_BG,
            fontFamily: C.sans,
            p: { xs: 1.5, sm: 2.5, md: 4 },
          }}
        >
          <Box sx={{ maxWidth: 920, mx: "auto" }}>
            {Header}
            <PersonaTeamList
              members={teamMembers}
              isLeafUser={isLeafUser}
              onSelect={openMember}
            />
          </Box>
        </Box>
      </>
    );
  }

  /* ── Auto-projects banner (for view & edit modes) ────────────────────── */
  const ProjectsBanner = (
    <Box
      sx={{
        mb: 2,
        background: "linear-gradient(135deg, #f0f9ff 0%, #eef2ff 100%)",
        border: `1px solid ${C.skyBd}`,
        borderRadius: 10,
        p: { xs: 1.75, md: 2.25 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box
          sx={{
            width: 28, height: 28, borderRadius: 999,
            background: C.sky, color: C.white,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: C.sans, fontWeight: 700, fontSize: 14,
            flexShrink: 0,
          }}
        >
          ✓
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: C.sans, fontWeight: 700,
              fontSize: { xs: 13.5, md: 14.5 }, color: C.txt1,
            }}
          >
            {projectIds.length === 0
              ? `${selectedUser?.name} has no projects assigned yet`
              : <>{selectedUser?.name}'s <b>{projectIds.length}</b> assigned project{projectIds.length === 1 ? "" : "s"} will be auto-included.</>}
          </Typography>
          {/* <Typography sx={{ fontFamily: C.sans, fontSize: 12.5, color: C.txt3, mt: 0.25 }}>
            {projectIds.length === 0
              ? "Projects will be matched once they're added to one."
              : "These projects stay visible while you tune the persona below."}
          </Typography> */}
          {/* {projectNames.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
              {projectNames.map((n) => (
                <Chip key={n} label={n} sx={projectChipSx} />
              ))}
            </Box>
          )} */}
        </Box>
      </Box>
    </Box>
  );

  /* ── VIEW MODE ───────────────────────────────────────────────────────── */
  if (mode === "view") {
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        <Box
          sx={{
            height: "100%",
            overflowY: "auto",
            background: PAGE_BG,
            fontFamily: C.sans,
            p: { xs: 1.5, sm: 2.5, md: 4 },
          }}
        >
          <Box sx={{ maxWidth: 920, mx: "auto" }}>
            {Header}
            {ProjectsBanner}

            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 1.75 } }}>
              <Section index={1} question="Languages spoken with customers"
                       icon={<TranslateIcon sx={{ fontSize: 18 }} />}>
                <ViewChips values={languages} />
              </Section>
              <Section index={2} question="Locations covered"
                       icon={<LocationOnOutlinedIcon sx={{ fontSize: 18 }} />}>
                <ViewChips values={locations} />
              </Section>
              <Section index={3} question="Unit types specialised in"
                       icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />}>
                <ViewChips values={unitTypes} />
              </Section>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                  gap: { xs: 1.5, md: 1.75 },
                }}
              >
                <Section question="Budget ranges handled" hint="Price bands customers usually fall in." compact>
                  <ViewChips values={budgetBands} />
                </Section>
                <Section question="Purchase purpose converted most" hint="Primary buying intent." compact>
                  <ViewChips values={purposes} labelMap={PURPOSE_LABEL} />
                </Section>
                <Section question="Gender preference for leads" hint="Pick Any if it does not matter." compact>
                  <ViewChips values={genderPrefs} />
                </Section>
              </Box>
            </Box>

            <FooterBar
              left={
                <>
                  <Typography sx={{ fontFamily: C.sans, fontWeight: 700, fontSize: 14, color: C.txt1 }}>
                    {totalSelected} preference{totalSelected === 1 ? "" : "s"} saved
                  </Typography>
                  <Typography sx={{ fontFamily: C.sans, fontSize: 12.5, color: C.txt3, mt: 0.25 }}>
                    + {projectIds.length} assigned project{projectIds.length === 1 ? "" : "s"} auto-included.
                  </Typography>
                </>
              }
              right={
                <PrimaryBtn type="button" onClick={() => setMode("edit")}>
                  Edit Persona
                </PrimaryBtn>
              }
            />
          </Box>
        </Box>
      </>
    );
  }

  /* ── EDIT MODE ───────────────────────────────────────────────────────── */
  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <Box
        sx={{
          height: "100%",
          overflowY: "auto",
          background: PAGE_BG,
          fontFamily: C.sans,
          p: { xs: 1.5, sm: 2.5, md: 4 },
        }}
      >
        <Box sx={{ maxWidth: 920, mx: "auto" }}>
          {Header}
          {ProjectsBanner}

          <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 1.75 } }}>

            <Section
              index={1}
              question="Which languages do they speak with customers?"
              hint="Select every language they can confidently handle."
              icon={<TranslateIcon sx={{ fontSize: 18 }} />}
            >
              <MultiSelect
                options={LANGUAGES}
                value={languages}
                onChange={setLanguages}
                placeholder="Search and add…"
              />
            </Section>

            <Section
              index={2}
              question="Which locations do they cover?"
              hint="Pick a state, then a city, then click Add."
              icon={<LocationOnOutlinedIcon sx={{ fontSize: 18 }} />}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr auto" },
                  gap: 1.25,
                  alignItems: "stretch",
                }}
              >
                <Autocomplete
                  size="small"
                  options={states}
                  loading={statesLoading}
                  value={stateValue}
                  onChange={(_, v) => setStateValue(v)}
                  renderInput={(params) => (
                    <TextField {...params} label="State" placeholder="Search state…" />
                  )}
                  sx={acSx}
                />
                <Autocomplete
                  size="small"
                  options={cityOptions}
                  loading={citiesLoading}
                  value={cityValue}
                  onChange={(_, v) => setCityValue(v)}
                  disabled={!stateValue}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      placeholder={stateValue ? "Search city…" : "Pick state first"}
                    />
                  )}
                  sx={acSx}
                />
                {/* <Autocomplete
                  size="small"
                  freeSolo
                  options={pincodeOptions}
                  loading={pincodesLoading}
                  value={pincodeValue}
                  onChange={(_, v) => setPincodeValue(v)}
                  onInputChange={(_, v, reason) => {
                    if (reason === "input") setPincodeValue(v || null);
                  }}
                  disabled={!cityValue}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Pincode (optional)"
                      placeholder={cityValue ? "Pick or type pincode" : "Pick city first"}
                    />
                  )}
                  sx={acSx}
                /> */}
                <AddBtn
                  type="button"
                  onClick={addLocation}
                  disabled={!stateValue || !cityValue}
                >
                  + Add
                </AddBtn>
              </Box>

              {locations.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    mt: 2,
                    pt: 2,
                    borderTop: `1px dashed ${C.border}`,
                  }}
                >
                  {locations.map((loc) => (
                    <Chip
                      key={loc}
                      label={loc}
                      onDelete={() =>
                        setLocations((prev) => prev.filter((v) => v !== loc))
                      }
                      sx={chipSx}
                    />
                  ))}
                </Box>
              )}
            </Section>

            <Section
              index={3}
              question="Which unit types do they specialise in?"
              hint="Choose the typologies they convert most often."
              icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />}
            >
              <MultiSelect
                options={unitTypeOptions}
                value={unitTypes}
                onChange={setUnitTypes}
                placeholder={unitTypesLoading ? "Loading unit types…" : "Search and add…"}
              />
            </Section>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: { xs: 1.5, md: 1.75 },
              }}
            >
              <Section
                question="Budget ranges they handle"
                hint="Price bands customers usually fall in."
                compact
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {budgetLoading && budgetOptions.length === 0 ? (
                    <Typography sx={{ fontFamily: C.sans, fontSize: 12.5, color: C.txt3 }}>
                      Loading budget options…
                    </Typography>
                  ) : budgetOptions.length === 0 ? (
                    <Typography sx={{ fontFamily: C.sans, fontSize: 12.5, color: C.txt3 }}>
                      No budget options configured. Add them in Settings → Dropdowns.
                    </Typography>
                  ) : (
                    budgetOptions.map((b) => (
                      <TogglePill
                        key={b.budgetId}
                        type="button"
                        active={budgetBands.includes(b.budget)}
                        onClick={() =>
                          setBudgetBands((prev) =>
                            prev.includes(b.budget)
                              ? prev.filter((v) => v !== b.budget)
                              : [...prev, b.budget]
                          )
                        }
                      >
                        {b.budget}
                      </TogglePill>
                    ))
                  )}
                </Box>
              </Section>

              <Section
                question="Purchase purpose they convert most"
                hint="Primary buying intent."
                compact
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {PURPOSES.map((p) => (
                    <TogglePill
                      key={p.value}
                      type="button"
                      active={purposes.includes(p.value)}
                      onClick={() =>
                        setPurposes((prev) =>
                          prev.includes(p.value) ? prev.filter((v) => v !== p.value) : [...prev, p.value]
                        )
                      }
                    >
                      {p.label}
                    </TogglePill>
                  ))}
                </Box>
              </Section>

              <Section
                question="Gender preference for leads"
                hint="Pick Any if it does not matter."
                compact
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {GENDER_PREFS.map((g) => (
                    <TogglePill
                      key={g.value}
                      type="button"
                      active={genderPrefs.includes(g.value)}
                      onClick={() =>
                        setGenderPrefs((prev) =>
                          prev.includes(g.value) ? prev.filter((v) => v !== g.value) : [...prev, g.value]
                        )
                      }
                    >
                      {g.label}
                    </TogglePill>
                  ))}
                </Box>
              </Section>
            </Box>

          </Box>

          <FooterBar
            left={
              <>
                <Typography sx={{ fontFamily: C.sans, fontWeight: 700, fontSize: 14, color: C.txt1 }}>
                  {totalSelected} preference{totalSelected === 1 ? "" : "s"} selected
                </Typography>
                <Typography sx={{ fontFamily: C.sans, fontSize: 12.5, color: C.txt3, mt: 0.25 }}>
                  + {projectIds.length} assigned project{projectIds.length === 1 ? "" : "s"} auto-included.
                </Typography>
              </>
            }
            right={
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  "& > *": { flex: { xs: 1, sm: "0 0 auto" } },
                }}
              >
                <GhostBtn type="button" onClick={handleCancel} disabled={saving}>
                  Cancel
                </GhostBtn>
                <PrimaryBtn type="button" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : hasExisting ? "Save Changes" : "Save Preferences"}
                </PrimaryBtn>
              </Box>
            }
          />

        </Box>
      </Box>
    </>
  );
}

/* ── Sticky footer wrapper ───────────────────────────────────────────────── */
function FooterBar({ left, right }) {
  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        mt: { xs: 2, md: 2.5 },
        mb: 2,
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: { xs: 8, md: 10 },
        p: { xs: 1.75, md: 2 },
        display: "flex",
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5,
        boxShadow: "0 -4px 16px rgba(15,23,42,0.05)",
      }}
    >
      <Box>{left}</Box>
      <Box sx={{ width: { xs: "100%", sm: "auto" } }}>{right}</Box>
    </Box>
  );
}

export default SalesManagerPersona;
